import React from 'react';
import { observer } from 'mobx-react';
import usePermissions, { Features } from '../../../permissions/usePermissions';
import ComponentNames from '../../../permissions/constants/componentNames';
import { useStore } from '../../../store/mst';
import { MiniRouteContainerType } from '../RouteInfo/MiniRouteInfo';
import { RouteDtoModelType } from '../../../store/mst/models';
import useMinutesLate from '../../../hooks/useMinutesLate';
import getBoundsFromRoutes from '../../../helpers/routes/getBoundsFromRoutes';
import { logRouteSelectionAttempt } from '../../../helpers/mapRecentering/logger';
import InnerRouteCard from './InnerRouteCard';

interface Props {
  route: RouteDtoModelType;
  stateless?: boolean;
  routeDetailsRef?: React.MutableRefObject<HTMLDivElement | null>;
  selectable?: boolean;
  withTitle?: boolean;
  withColorBar?: boolean;
  withAssignDriver?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, uuid?: string, index?: number) => void;
  index?: number;
  withStats?: boolean;
}

const StandardRouteCard: React.FC<Props> = ({
  route,
  stateless,
  routeDetailsRef,
  selectable,
  withTitle = false,
  withColorBar = false,
  withAssignDriver,
  index,
  onClick,
  withStats,
}) => {
  const store = useStore();
  const { ui } = store;
  const containerType = stateless ? MiniRouteContainerType.BREAKDOWN : MiniRouteContainerType.OVERVIEW;
  const isSynced = ui.selectedWorkspace?.isSynced;

  const {
    canEditRoutes,
  } = usePermissions(Features.Routes, ComponentNames.StandardRouteCard);

  const isCalendarView = !!ui.selectedCalendarView;
  const showCheckbox = canEditRoutes && selectable && !isSynced && !isCalendarView;
  const isChecked = ui.selectedRoutesBulk.has(route.uuid || '');

  const pending = route.isBeingModified;
  const minutesLate = useMinutesLate((route.lateEvent?.isRouteLate && !!route.isStarted) || false, route.lateEvent?.eventTime, route.lateEvent?.expectedTime);

  const handleHoverCard = (hovered: boolean) => {
    if (!stateless) {
      ui.hoverRoute(hovered ? route.id : null);
    }
  };

  const handleClickCard = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (!stateless) {
      onClick?.(e, route.uuid!, index);
      const routeBounds = getBoundsFromRoutes([route]);

      if (routeBounds) {
        logRouteSelectionAttempt({
          route,
          bounds: routeBounds,
          triggerSource: 'StandardRouteCard_click',
        });
        store.mapView.setMapPosition(routeBounds);
      }
    }
  };

  const onChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      ui.insertSelectedRoutesBulk(e.target.value);
    } else {
      ui.deleteSelectedRoutesBulk(e.target.value);
    }
  };

  return (
    <InnerRouteCard
      handleHoverCard={handleHoverCard}
      handleClickCard={handleClickCard}
      stateless={stateless}
      isHovered={!stateless ? route.isHovered : false}
      selected={!stateless ? route.isSelected : false}
      route={route}
      routeDetailsRef={routeDetailsRef}
      withTitle={withTitle}
      withColorBar={withColorBar}
      pending={pending}
      minutesLate={minutesLate}
      containerType={containerType}
      withAssignDriver={withAssignDriver}
      withStats={withStats}
      showCheckbox={showCheckbox}
      isChecked={isChecked}
      onCheckboxChange={onChangeCheckbox}
      routeUuid={route.uuid}
    />
  );
};

export default observer(StandardRouteCard);
